import type { Extensions } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import HardBreak from "@tiptap/extension-hard-break";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Typography from "@tiptap/extension-typography";
import {
  APOSTROPHE,
  BOLD,
  BULLET_LIST,
  DIRECTIVE,
  DIV,
  EMPHASIZED,
  ITALIC,
  ORDERED_LIST,
  STRONG,
  UNDOREDO,
  VARIABLE,
  type ToolbarButtonId,
  DEFAULT_TOOLBAR_BUTTONS,
} from "./toolbar/shared/richTextEditorToolbarConstants";
import { VariableTokenExtension } from "./variables/variableTokenExtension";
import { VariableSuggestionExtension } from "./variables/variableSuggestionExtension";
import { DirectiveSuggestionExtension } from "./directives/suggestion/extension/extension";
import type { Variables } from "@/lib/sparxs/metadataSchemas";

export const RteFeature = {
  bold: "bold",
  italic: "italic",
  strong: "strong",
  emphasized: "emphasized",
  bulletList: "bulletList",
  orderedList: "orderedList",
  horizontalRule: "horizontalRule",
  apostrophe: "apostrophe",
  variable: "variable",
  directive: "directive",
  undoRedo: "undoRedo",
  typography: "typography",
  editorModeTabs: "editorModeTabs",
  headerTrailingHelp: "headerTrailingHelp",
  bubbleMenu: "bubbleMenu",
} as const;

export type RteFeature = (typeof RteFeature)[keyof typeof RteFeature];

/** Dynamic grid image URL: undo/redo + variables only; forced WYSIWYG; minimal chrome. */
export const RTE_FEATURE_BLACKLIST_MINIMAL: readonly RteFeature[] = [
  RteFeature.bold,
  RteFeature.italic,
  RteFeature.strong,
  RteFeature.emphasized,
  RteFeature.bulletList,
  RteFeature.orderedList,
  RteFeature.horizontalRule,
  RteFeature.apostrophe,
  RteFeature.directive,
  RteFeature.typography,
  RteFeature.editorModeTabs,
  RteFeature.headerTrailingHelp,
  RteFeature.bubbleMenu,
];

/** No bullet / numbered lists or horizontal rule (toolbar, extensions, bubble). */
export const RTE_FEATURE_BLACKLIST_COMPACT: readonly RteFeature[] = [
  RteFeature.bulletList,
  RteFeature.orderedList,
  RteFeature.horizontalRule,
];

const setsEqual = <T>(a: Set<T>, b: Set<T>): boolean => {
  if (a.size !== b.size) return false;
  for (const x of a) {
    if (!b.has(x)) return false;
  }
  return true;
};

export const matchesMinimalFeatureBlacklist = (
  blacklist: readonly RteFeature[] | undefined,
): boolean => {
  if (blacklist == null || blacklist.length === 0) return false;
  return setsEqual(new Set(blacklist), new Set(RTE_FEATURE_BLACKLIST_MINIMAL));
};

const toolbarButtonToFeature = (
  id: ToolbarButtonId,
): RteFeature | undefined => {
  switch (id) {
    case UNDOREDO:
      return RteFeature.undoRedo;
    case BOLD:
      return RteFeature.bold;
    case ITALIC:
      return RteFeature.italic;
    case STRONG:
      return RteFeature.strong;
    case EMPHASIZED:
      return RteFeature.emphasized;
    case BULLET_LIST:
      return RteFeature.bulletList;
    case ORDERED_LIST:
      return RteFeature.orderedList;
    case APOSTROPHE:
      return RteFeature.apostrophe;
    case DIV:
      return RteFeature.horizontalRule;
    case VARIABLE:
      return RteFeature.variable;
    case DIRECTIVE:
      return RteFeature.directive;
    default:
      return undefined;
  }
};

/** Toolbar buttons still allowed after applying the blacklist (subset of default order). */
export const toolbarButtonsForFeatureBlacklist = (
  blacklist: readonly RteFeature[] | undefined,
): ToolbarButtonId[] => {
  if (blacklist == null || blacklist.length === 0) {
    return [...DEFAULT_TOOLBAR_BUTTONS];
  }
  const blocked = new Set(blacklist);
  return DEFAULT_TOOLBAR_BUTTONS.filter((id) => {
    const f = toolbarButtonToFeature(id);
    return f === undefined || !blocked.has(f);
  });
};

export const featureBlacklistHas = (
  blacklist: readonly RteFeature[] | undefined,
  feature: RteFeature,
): boolean =>
  blacklist != null && blacklist.length > 0 && blacklist.includes(feature);

/** Union of blacklisted features from any non-empty list (stricter = more features disabled). */
export const mergeFeatureBlacklists = (
  ...lists: (readonly RteFeature[] | undefined)[]
): readonly RteFeature[] | undefined => {
  const set = new Set<RteFeature>();
  for (const list of lists) {
    if (list == null || list.length === 0) continue;
    for (const f of list) set.add(f);
  }
  if (set.size === 0) return undefined;
  return [...set];
};

export const buildWysiwygExtensionsForFeatureBlacklist = (
  variables: Variables[],
  blacklist: readonly RteFeature[] | undefined,
): Extensions => {
  if (matchesMinimalFeatureBlacklist(blacklist)) {
    return [
      Document,
      Paragraph,
      Text,
      HardBreak,
      History,
      VariableSuggestionExtension.configure({ char: "{", variables }),
      VariableTokenExtension.configure({ variables }),
    ];
  }

  const b = new Set(blacklist ?? []);
  const extensions: Extensions = [
    Document,
    Paragraph,
    Text,
    HardBreak,
    History,
  ];

  if (!b.has(RteFeature.typography)) {
    extensions.push(Typography);
  }
  if (!b.has(RteFeature.bold)) {
    extensions.push(Bold);
  }
  if (!b.has(RteFeature.italic)) {
    extensions.push(Italic);
  }
  if (!b.has(RteFeature.horizontalRule)) {
    extensions.push(HorizontalRule);
  }

  let needsListItem = false;
  if (!b.has(RteFeature.bulletList)) {
    extensions.push(BulletList);
    needsListItem = true;
  }
  if (!b.has(RteFeature.orderedList)) {
    extensions.push(OrderedList);
    needsListItem = true;
  }
  if (needsListItem) {
    extensions.push(ListItem);
  }

  if (!b.has(RteFeature.variable)) {
    extensions.push(
      VariableSuggestionExtension.configure({ char: "{", variables }),
      VariableTokenExtension.configure({ variables }),
    );
  }

  if (!b.has(RteFeature.directive)) {
    extensions.push(DirectiveSuggestionExtension.configure({ variables }));
  }

  return extensions;
};
